import React, { useState, useEffect } from "react";
import { Stack, Toggle } from "@fluentui/react";
import ViewCopyDragDropZone from "../../../../../components/checkinDocuments/ViewCopyDragDropZone";
import ViewCopyRecord from "../../../../../components/checkinDocuments/ViewCopyRecord";
import { useTheme } from "../../../../../theme/OAS365Theme";
import { useViewCopyStyle } from "../../../../../theme/styles/ViewCopyStyle";
import { useUploadFilesItem } from "../../../../../theme/styles/UploadFilesItemStyle";
import {
  CREATE_VIEW_COPY_SUPPORT,
  FILE_AUTO_ENABLE,
  FILE_AUTO_ENABLE_SUB,
} from "../../../../../redux/constants/metaData";
import { CHECKIN_VALIDATION_VIEW_COPY_REQUIRED } from "../../../../../redux/constants/messageTypes";
import { resolveCreateViewCopyDefault } from "../../../../../utils/resolveCreateViewCopyDefault";
import { useCheckin } from "../../../../../redux/hooks/useCheckin";
import { useWorkingDocument } from "../../../../../redux/hooks/useWorkingDocument";
import { useTools } from "../../../../../redux/hooks/useTools";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AccordionSummaryWithValidation,
  getAccordionSummarySx,
} from "../../../../../components/checkinDocuments/AccordionSummaryWithValidation";

/**
 * View Copy accordion for the working document details dialog.
 * @param {Object} [props.workingIfsDocument] - Optional document/revision data (e.g. from details dialog).
 *   When provided, used for Word vs non-Word and viewRequired logic instead of useWorkingDocument().
 * @param {boolean|null} [props.isViewCopyExists] - IFS VIEW copy exists; when boolean, toggle defaults to match (false → auto-generate off; true → on).
 */
const ViewCopyAccordion = ({
  workingIfsDocument: workingIfsDocumentProp,
  noTopMargin = false,
  isViewCopyExists = null,
  showFieldValidation = true,
}) => {
  const {
    createViewCopyDisable,
    createViewCopy,
    uploadFiles = [],
    uploadViewCopyCount = 0,
    docClassFormatList,
    viewRequired,
    viewCopyFiles = [],
    onBasicDataLoad,
  } = useCheckin();
  const { workingIfsDocument: workingIfsDocumentFromHook } = useWorkingDocument();
  const workingIfsDocument = workingIfsDocumentProp ?? workingIfsDocumentFromHook;
  const tools = useTools();
  const {
    onDocClassFormatListChange,
    onCreateViewCopyChange,
    onViewCopyUploadChange,
  } = tools;
  const theme = useTheme();
  const accordianThemeStyle = useViewCopyStyle();
  const fileStyleTheme = useUploadFilesItem();
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [computedViewRequired, setComputedViewRequired] = useState(false);

  useEffect(() => {
    if (
      !docClassFormatList ||
      !Array.isArray(docClassFormatList) ||
      !workingIfsDocument
    ) {
      setComputedViewRequired(false);
      return;
    }

    const docClass = workingIfsDocument.docClass;
    if (!docClass) {
      setComputedViewRequired(false);
      return;
    }

    let foundViewRequired = false;

    for (const item of docClassFormatList) {
      if (item.code === docClass && item.viewRequired === "Y") {
        foundViewRequired = true;
        break;
      }

      if (item.children && Array.isArray(item.children)) {
        for (const child of item.children) {
          if (child.code === docClass && child.viewRequired === "Y") {
            foundViewRequired = true;
            break;
          }
        }
      }

      if (foundViewRequired) break;
    }
    setComputedViewRequired(foundViewRequired);
  }, [docClassFormatList, workingIfsDocument]);

  const finalViewRequired = computedViewRequired || viewRequired;

  const isWordDocument = () => {
    if (!workingIfsDocument || Object.keys(workingIfsDocument).length === 0) return false;
    const ext = (workingIfsDocument.FileExtension ?? workingIfsDocument.fileExtension ?? "").toUpperCase();
    return ext === "DOCX" || ext === "DOC" || ext === "DOCM";
  };

  const shouldShowAutoGenerateToggle = isWordDocument();

  useEffect(() => {
    const ifsReportsViewCopy = typeof isViewCopyExists === "boolean";
    if (
      finalViewRequired ||
      (ifsReportsViewCopy && shouldShowAutoGenerateToggle)
    ) {
      setIsAccordionExpanded(true);
    } else {
      setIsAccordionExpanded(false);
    }
  }, [finalViewRequired, isViewCopyExists, shouldShowAutoGenerateToggle]);

  const handleAccordionChange = (event, isExpanded) => {
    setIsAccordionExpanded(isExpanded);
  };

  useEffect(() => {
    if (uploadViewCopyCount !== 0 || !onCreateViewCopyChange) {
      return;
    }

    const ifsViewCopyKnown = typeof isViewCopyExists === "boolean";

    if (ifsViewCopyKnown) {
      onCreateViewCopyChange(
        resolveCreateViewCopyDefault({
          isViewCopyExists,
          viewRequired: finalViewRequired,
          isWordLike: shouldShowAutoGenerateToggle,
          uploadViewCopyCount,
        }),
      );
      return;
    }

    if (!finalViewRequired) {
      return;
    }

    if (!shouldShowAutoGenerateToggle) {
      onCreateViewCopyChange(false);
      return;
    }

    // viewRequired but legacy API without isViewCopyExists
    onCreateViewCopyChange(true);
  }, [
    finalViewRequired,
    uploadViewCopyCount,
    onCreateViewCopyChange,
    shouldShowAutoGenerateToggle,
    isViewCopyExists,
  ]);

  const getViewCopyToolTip = () => {
    if (workingIfsDocument !== undefined && workingIfsDocument !== null) {
      const ext = (workingIfsDocument.FileExtension ?? workingIfsDocument.fileExtension ?? "").toUpperCase();
      return ext === "DOCX" || ext === "DOC" || ext === "DOCM"
        ? ""
        : CREATE_VIEW_COPY_SUPPORT;
    }
    return CREATE_VIEW_COPY_SUPPORT;
  };

  const hasUploadedFiles = uploadViewCopyCount > 0;
  const leftBoxHeight = "147px";
  const rightBoxHeight = "180px";
  const uploadBoxHeight = hasUploadedFiles ? "110px" : rightBoxHeight;

  const toggleValue = createViewCopy;

  const isUploadDisabled = shouldShowAutoGenerateToggle && toggleValue === true;

  const viewCopyError =
    showFieldValidation &&
    finalViewRequired &&
    uploadViewCopyCount === 0 &&
    !createViewCopy
      ? CHECKIN_VALIDATION_VIEW_COPY_REQUIRED
      : "";

  const handleToggleChange = (ev, check) => {
    if (onCreateViewCopyChange) {
      onCreateViewCopyChange(check);
    }
    // Only Word documents support auto-generate; never clear manual PDF uploads for Excel/PPT/etc.
    if (
      shouldShowAutoGenerateToggle &&
      check === true &&
      viewCopyFiles &&
      viewCopyFiles.length > 0
    ) {
      tools.onViewCopyUploadChange([], 0, 0, finalViewRequired, uploadFiles);
    }
  };

  useEffect(() => {
    if (
      shouldShowAutoGenerateToggle &&
      toggleValue === true &&
      viewCopyFiles &&
      viewCopyFiles.length > 0
    ) {
      tools.onViewCopyUploadChange([], 0, 0, finalViewRequired, uploadFiles);
    }
  }, [
    shouldShowAutoGenerateToggle,
    toggleValue,
    viewCopyFiles,
    finalViewRequired,
    uploadFiles,
    onViewCopyUploadChange,
  ]);

  return (
    <Stack.Item grow align="center" style={{ marginTop: noTopMargin ? 0 : "25px" }}>
      <Accordion
        style={accordianThemeStyle.accordianStyle}
        expanded={isAccordionExpanded}
        onChange={handleAccordionChange}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
          aria-controls="view-copy-content"
          id="view-copy-header"
          sx={getAccordionSummarySx(!!viewCopyError)}
        >
          <AccordionSummaryWithValidation
            title="View Copy"
            error={viewCopyError}
            titleStyle={accordianThemeStyle.accordianTextStyle}
          />
        </AccordionSummary>
        <AccordionDetails style={accordianThemeStyle.accordianDetails}>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
              alignItems: "stretch",
              flexDirection: "row",
            }}
          >
            {shouldShowAutoGenerateToggle && (
              <Box
                style={accordianThemeStyle.accordianBoxStyle}
                sx={{ height: leftBoxHeight }}
              >
                <Box>
                  <Toggle
                    style={{ fontFamily: theme.fonts.regular }}
                    disabled={false}
                    checked={toggleValue}
                    onChange={handleToggleChange}
                    onText={
                      <div
                        style={{
                          fontFamily: theme.fonts.regular,
                          fontSize: theme.fontSizes.normal,
                        }}
                      >
                        {getViewCopyToolTip()}
                      </div>
                    }
                    offText={
                      <div
                        style={{
                          fontFamily: theme.fonts.regular,
                          fontSize: theme.fontSizes.normal,
                        }}
                      >
                        {getViewCopyToolTip()}
                      </div>
                    }
                  />
                </Box>

                <Typography style={accordianThemeStyle.viewCopyTextStyle}>
                  {FILE_AUTO_ENABLE}
                  <br />
                  {FILE_AUTO_ENABLE_SUB}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                width: "100%",
                height: rightBoxHeight,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack disableShrink style={{ height: "100%" }}>
                  <Stack.Item
                    styles={fileStyleTheme.ViewCopyStackStyle}
                    sx={{
                      flex: hasUploadedFiles ? 0.6 : 1,
                      transition: "flex 0.3s ease",
                      minHeight: uploadBoxHeight,
                    }}
                  >
                    <ViewCopyDragDropZone
                      isDisabled={isUploadDisabled}
                      boxHeight={uploadBoxHeight}
                    />
                  </Stack.Item>
                  {hasUploadedFiles && (
                    <Stack.Item
                      sx={{
                        flex: 0.4,
                        transition: "flex 0.3s ease",
                      }}
                    >
                      <ViewCopyRecord />
                    </Stack.Item>
                  )}
                </Stack>
              </Box>
            </Box>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack.Item>
  );
};

export default ViewCopyAccordion;
